import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  FaFacebook,
  FaInstagram,
  FaPinterestSquare,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";
import {
  FiChevronDown,
  FiMail,
  FiMapPin,
  FiMenu,
  FiPhone,
  FiSearch,
  FiUser,
  FiX,
} from "react-icons/fi";
import logo from "../assets/logo.png";

const menuItems = [
  { name: "Home", path: "/" },
  {
    name: "Destinations",
    path: "/destinations",
    featured: "Explore Uganda and East Africa's most iconic wild places.",
    columns: [
      {
        title: "National Parks",
        links: [
          { name: "Bwindi Impenetrable Forest", path: "/destinations" },
          { name: "Murchison Falls", path: "/destinations" },
          { name: "Queen Elizabeth Park", path: "/destinations" },
          { name: "Kibale Forest", path: "/destinations" },
        ],
      },
      {
        title: "Regional Highlights",
        links: [
          { name: "Kenya Safaris", path: "/destinations" },
          { name: "Tanzania Adventures", path: "/destinations" },
          { name: "Namibia Desert Tours", path: "/tours" },
          { name: "Rwanda Gorilla Tours", path: "/destinations" },
        ],
      },
    ],
  },
  {
    name: "Tours & Safaris",
    path: "/tours",
    featured: "Curated journeys for wildlife, primates, families, and couples.",
    columns: [
      {
        title: "Safari Styles",
        links: [
          { name: "Gorilla Trekking", path: "/tours" },
          { name: "Wildlife Safaris", path: "/tours" },
          { name: "Luxury Safaris", path: "/tours" },
          { name: "Desert & Coast Tours", path: "/tours" },
          { name: "Family Holidays", path: "/tours" },
        ],
      },
      {
        title: "Travel Ideas",
        links: [
          { name: "Honeymoon Trips", path: "/tours" },
          { name: "Adventure Tours", path: "/tours" },
          { name: "Birding Experiences", path: "/tours" },
          { name: "Weekend Escapes", path: "/tours" },
        ],
      },
    ],
  },
  { name: "Gallery", path: "/gallery" },
  { name: "Blogs", path: "/blogs" },
  { name: "Contact", path: "/contact" },
];

const Navbar = () => {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setNavOpen(false);
    setActiveDropdown(null);
    setMobileDropdown(null);
  }, [location.pathname]);

  const headerIsSolid = !isHome || scrolled || navOpen;

  const navLinkClass = ({ isActive }) =>
    `relative flex h-16 items-center px-1 text-[13px] font-semibold uppercase tracking-[0.12em] transition ${
      isActive ? "text-[#F5B731]" : "text-white hover:text-[#F5B731]"
    }`;

  return (
    <header
      className={`fixed left-0 top-0 z-50 w-full font-[Poppins] text-white transition-all duration-300 ${
        headerIsSolid ? "bg-[#082f1f] shadow-xl" : "bg-black/20"
      }`}
    >
      <div className="hidden border-b border-white/10 bg-[#061f16]/95 px-6 text-xs lg:block">
        <div className="mx-auto flex h-10 max-w-7xl items-center justify-between">
          <div className="flex items-center gap-6 text-white/80">
            <span className="flex items-center gap-2">
              <FiPhone className="text-[#F5B731]" />
              +256 772 642 101
            </span>
            <span className="flex items-center gap-2">
              <FiMail className="text-[#F5B731]" />
              info@gorillandtravels.com
            </span>
            <span className="flex items-center gap-2">
              <FiMapPin className="text-[#F5B731]" />
              Kampala, Uganda
            </span>
          </div>

          <div className="flex items-center gap-4 text-white/75">
            <NavLink to="/blogs" className="hover:text-[#F5B731]">
              Travel Updates
            </NavLink>
            <NavLink to="/contact" className="hover:text-[#F5B731]">
              Help Desk
            </NavLink>
          </div>
        </div>
      </div>

      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <NavLink to="/" className="flex items-center gap-3" aria-label="Gorilland home">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white p-1 shadow-lg">
            <img src={logo} className="h-full w-full object-contain" alt="Gorilland logo" />
          </span>
          <span className="hidden leading-tight sm:block">
            <span className="block text-base font-bold uppercase tracking-[0.16em]">
              Gorilland
            </span>
            <span className="block text-xs font-medium text-[#F5B731]">
              Tours & Travel
            </span>
          </span>
        </NavLink>

        <nav className="hidden items-center lg:flex" aria-label="Main navigation">
          {menuItems.map((item) => {
            const hasDropdown = Boolean(item.columns);
            const isOpen = activeDropdown === item.name;

            return (
              <div
                key={item.name}
                className="relative px-3"
                onMouseEnter={() => hasDropdown && setActiveDropdown(item.name)}
                onMouseLeave={() => hasDropdown && setActiveDropdown(null)}
              >
                <div className="flex items-center gap-1">
                  <NavLink to={item.path} className={navLinkClass}>
                    {item.name}
                    <span className="absolute bottom-3 left-0 h-[2px] w-0 bg-[#F5B731] transition-all duration-300 group-hover:w-full" />
                  </NavLink>
                  {hasDropdown && (
                    <button
                      type="button"
                      onClick={() => setActiveDropdown(isOpen ? null : item.name)}
                      className="rounded-full p-1 text-[#F5B731] transition hover:bg-white/10"
                      aria-label={`Toggle ${item.name} menu`}
                      aria-expanded={isOpen}
                    >
                      <FiChevronDown
                        className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                  )}
                </div>

                {hasDropdown && isOpen && (
                  <div className="absolute left-1/2 top-full w-[760px] -translate-x-1/2 pt-2">
                    <div className="overflow-hidden rounded-sm border border-black/5 bg-white text-[#082f1f] shadow-2xl">
                      <div className="grid grid-cols-[1fr_2fr]">
                        <div className="bg-[#F5B731] p-6">
                          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#082f1f]/70">
                            {item.name}
                          </p>
                          <p className="mt-3 text-xl font-bold leading-snug">{item.featured}</p>
                          <NavLink
                            to={item.path}
                            className="mt-6 inline-flex items-center rounded-sm bg-[#082f1f] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#12452f]"
                          >
                            View all
                          </NavLink>
                        </div>

                        <div className="grid grid-cols-2 gap-8 p-6">
                          {item.columns.map((column) => (
                            <div key={column.title}>
                              <h3 className="border-b border-[#F5B731]/50 pb-2 text-sm font-bold uppercase tracking-[0.16em]">
                                {column.title}
                              </h3>
                              <ul className="mt-3 space-y-1">
                                {column.links.map((link) => (
                                  <li key={link.name} className="p-0 text-sm">
                                    <NavLink
                                      to={link.path}
                                      className="block rounded-sm px-2 py-2 font-medium text-[#315245] transition hover:bg-[#f6efe1] hover:text-[#082f1f]"
                                    >
                                      {link.name}
                                    </NavLink>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <button
            type="button"
            className="grid h-10 w-10 place-items-center rounded-full border border-white/20 text-white transition hover:border-[#F5B731] hover:text-[#F5B731]"
            aria-label="Search"
          >
            <FiSearch />
          </button>
          <button
            type="button"
            className="grid h-10 w-10 place-items-center rounded-full border border-white/20 text-white transition hover:border-[#F5B731] hover:text-[#F5B731]"
            aria-label="Account"
          >
            <FiUser />
          </button>
          <NavLink
            to="/contact"
            className="rounded-sm bg-[#F5B731] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#082f1f] shadow-lg transition hover:bg-white"
          >
            Book Now
          </NavLink>
        </div>

        <button
          type="button"
          onClick={() => setNavOpen((open) => !open)}
          className="grid h-11 w-11 place-items-center rounded-sm border border-white/20 text-2xl text-white lg:hidden"
          aria-label="Toggle navigation"
          aria-expanded={navOpen}
        >
          {navOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      <div
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-[#082f1f] px-5 py-5 shadow-2xl transition-transform duration-300 lg:hidden ${
          navOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-white/10 pb-5">
          <NavLink to="/" className="flex items-center gap-3">
            <img src={logo} className="h-12 w-12 rounded-full bg-white object-contain p-1" alt="Logo" />
            <span className="text-sm font-bold uppercase tracking-[0.16em]">Gorilland</span>
          </NavLink>
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            className="grid h-10 w-10 place-items-center rounded-sm bg-white/10 text-xl"
            aria-label="Close navigation"
          >
            <FiX />
          </button>
        </div>

        <nav className="mt-5 h-[calc(100vh-150px)] overflow-y-auto pb-8">
          {menuItems.map((item) => {
            const hasDropdown = Boolean(item.columns);
            const isOpen = mobileDropdown === item.name;

            return (
              <div key={item.name} className="border-b border-white/10 py-1">
                <div className="flex items-center justify-between">
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `block py-4 text-sm font-bold uppercase tracking-[0.14em] ${
                        isActive ? "text-[#F5B731]" : "text-white"
                      }`
                    }
                  >
                    {item.name}
                  </NavLink>

                  {hasDropdown && (
                    <button
                      type="button"
                      onClick={() => setMobileDropdown(isOpen ? null : item.name)}
                      className="grid h-9 w-9 place-items-center rounded-sm bg-white/10 text-[#F5B731]"
                      aria-label={`Toggle ${item.name} links`}
                      aria-expanded={isOpen}
                    >
                      <FiChevronDown
                        className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                  )}
                </div>

                {hasDropdown && isOpen && (
                  <div className="pb-4">
                    {item.columns.map((column) => (
                      <div key={column.title} className="mt-3 rounded-sm bg-white/5 p-4">
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#F5B731]">
                          {column.title}
                        </p>
                        <ul className="mt-2 space-y-1">
                          {column.links.map((link) => (
                            <li key={link.name} className="p-0 text-sm">
                              <NavLink
                                to={link.path}
                                className="block py-2 text-white/80 hover:text-[#F5B731]"
                              >
                                {link.name}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <NavLink
            to="/contact"
            className="mt-6 block rounded-sm bg-[#F5B731] px-5 py-3 text-center text-sm font-bold uppercase tracking-[0.14em] text-[#082f1f]"
          >
            Book Now
          </NavLink>

          <div className="mt-6 flex justify-center gap-5 text-xl text-white/70">
            <FaFacebook className="transition hover:text-[#F5B731]" />
            <FaTwitter className="transition hover:text-[#F5B731]" />
            <FaInstagram className="transition hover:text-[#F5B731]" />
            <FaPinterestSquare className="transition hover:text-[#F5B731]" />
            <FaYoutube className="transition hover:text-[#F5B731]" />
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
